/** Highlighting entry: grouping, validation and apply function */

import { ReactNode } from 'react';
import { AstNode, AstText, BlockIndex, TextRunLookup } from '../types';
import { renderAst } from '../renderer';
import { Annotation } from '../../../Annotation/types';
import { renderTextNodeWithHighlights } from './rendering';
import type { BlockAnnotationsLookup } from './types';

/** Returns existing group of annotations in block or creates an empty one. */
function getOrCreateBlockAnnotationGroup(
  annotationsByBlock: BlockAnnotationsLookup,
  blockId: BlockIndex,
): Annotation[] {
  const existing = annotationsByBlock.get(blockId);
  if (existing) {
    return existing;
  }
  annotationsByBlock.set(blockId, []);
  return annotationsByBlock.get(blockId) as Annotation[];
}

/** Groups annotations by block ID for efficient processing. */
function groupAnnotationsByBlock(
  annotations: Annotation[],
  textRunLookup: TextRunLookup,
): BlockAnnotationsLookup {
  const groupedAnnotations = new Map<BlockIndex, Annotation[]>();
  /** All block IDs that have text runs */
  const allBlockIds = Array.from(textRunLookup.keys());

  for (const annotation of annotations) {
    const { start, end } = annotation.position;

    if (start.blockId === end.blockId) {
      const target = start.blockId;
      getOrCreateBlockAnnotationGroup(groupedAnnotations, target).push(
        annotation,
      );
    } else {
      // Add the annotation to all the block annotation groups that the annotation spans across
      const startIdx = allBlockIds.indexOf(start.blockId);
      const endIdx = allBlockIds.indexOf(end.blockId);
      for (let i = startIdx; i <= endIdx; i++) {
        getOrCreateBlockAnnotationGroup(
          groupedAnnotations,
          allBlockIds[i],
        ).push(annotation);
      }
    }
  }

  return groupedAnnotations;
}

/** Standalone text renderer that applies highlights using provided lookup and grouping */
function renderTextHook(
  textRunLookup: TextRunLookup,
  groupedAnnotations: BlockAnnotationsLookup,
) {
  return (textNode: AstText): ReactNode => {
    const { blockId } = textNode;

    /* If there are no annotations for the text node, we return just the text.
    If there are annotations, we render them with rendering function. */
    if (!blockId) {
      return textNode.text;
    }
    const blockAnnotations = groupedAnnotations.get(blockId) || [];
    if (blockAnnotations.length === 0) {
      return textNode.text;
    }

    return renderTextNodeWithHighlights(
      textNode,
      textRunLookup,
      blockAnnotations,
    );
  };
}

/** Applies highlights to AST using per-block annotation grouping and text rendering. */
export function renderAstWithHighlights(
  ast: AstNode[],
  textRunLookup: TextRunLookup,
  annotations: Annotation[],
): ReactNode[] {
  const annotationsByBlock = groupAnnotationsByBlock(
    annotations,
    textRunLookup,
  );

  return renderAst(
    ast,
    renderTextHook(textRunLookup, annotationsByBlock),
    annotationsByBlock,
  );
}
